import { useState } from 'react'
import { api, useQuery } from '../api.js'
import { Button, Medidor, Panel, Pill, Sparkline, bytesHumano, uptimeHumano } from '../components/ui.js'

type Servidor = Awaited<ReturnType<typeof api.fleet.servers.query>>[number]

/**
 * Monitor — a visao geral e moderna do servidor.
 *
 * Um cartao por maquina, com CPU/memoria/disco de relance e o estado do
 * agente. Clicar abre os graficos das ultimas 24 h. A pergunta que esta tela
 * responde num segundo e "esta tudo bem la?", sem abrir RDP em nada.
 *
 * Sem agente instalado, o cartao diz isso — a coleta vem do agente, e a tela
 * nao inventa numero que nao tem.
 */
export function Monitor({ onNavigate }: { onNavigate: (tab: 'frota' | 'contas') => void }) {
  const servers = useQuery(() => api.fleet.servers.query(), [], { pollMs: 15_000 })
  const metrics = useQuery(() => api.identity.metrics.query(), [], { pollMs: 15_000 })
  const agentes = useQuery(() => api.identity.agents.query(), [], { pollMs: 15_000 })
  const [aberto, setAberto] = useState<string | null>(null)

  const lista = servers.data ?? []
  const porServidor = metrics.data ?? {}
  const agentesLista = agentes.data ?? []
  const agentePorServidor = new Map(agentesLista.map((a) => [a.serverId, a]))
  const algumAgenteOnline = agentesLista.some((a) => a.status === 'online')

  return (
    <div className="space-y-6">
      <PrimeirosPassos
        temServidor={lista.length > 0}
        temAgente={algumAgenteOnline}
        onNavigate={onNavigate}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {lista.map((s) => (
          <CartaoServidor
            key={s.id}
            servidor={s}
            metrica={porServidor[s.id]}
            agenteOnline={agentePorServidor.get(s.id)?.status === 'online'}
            aberto={aberto === s.id}
            onAbrir={() => setAberto(aberto === s.id ? null : s.id)}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Primeiros passos — o guia que faltava.
 *
 * O relato foi "simplesmente nao funciona a criacao". Quase sempre isso e:
 * cheguei numa tela e nao sabia o proximo passo. Este bloco mostra a trilha
 * inteira, marca o que ja esta feito, e leva ao lugar certo com um clique.
 * Some quando tudo esta pronto — nao vira enfeite permanente.
 */
function PrimeirosPassos({
  temServidor,
  temAgente,
  onNavigate,
}: {
  temServidor: boolean
  temAgente: boolean
  onNavigate: (tab: 'frota' | 'contas') => void
}) {
  if (temServidor && temAgente) return null

  const passos = [
    {
      feito: temServidor,
      titulo: 'Ter um servidor na frota',
      texto: 'Cadastre um provedor e sincronize, ou adicione um servidor a mao (um VPS avulso).',
      acao: () => onNavigate('frota'),
      rotulo: 'Ir para a Frota',
    },
    {
      feito: temAgente,
      titulo: 'Instalar o agente no servidor',
      texto: 'Na aba Contas, baixe o instalador e rode uma vez no servidor. Sem ele, nao ha metrica nem criacao de conta.',
      acao: () => onNavigate('contas'),
      rotulo: 'Ir para Contas',
    },
    {
      feito: false,
      titulo: 'Criar e gerenciar contas',
      texto: 'Com o agente online, criar usuario, trocar senha e administrar acesso passa a funcionar pela tela.',
      acao: () => onNavigate('contas'),
      rotulo: 'Abrir Contas',
    },
  ]
  const proximo = passos.findIndex((p) => !p.feito)

  return (
    <Panel title="Primeiros passos">
      <ol className="space-y-3">
        {passos.map((p, i) => (
          <li key={i} className="flex items-start gap-3">
            <span
              className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-bold ${
                p.feito ? 'bg-ok-soft text-ok' : i === proximo ? 'bg-accent text-white' : 'bg-ground text-ink-3'
              }`}
            >
              {p.feito ? '✓' : i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-ink">{p.titulo}</div>
              <div className="text-xs text-ink-3">{p.texto}</div>
            </div>
            {i === proximo && (
              <Button variant="primary" onClick={p.acao}>
                {p.rotulo}
              </Button>
            )}
          </li>
        ))}
      </ol>
    </Panel>
  )
}

function CartaoServidor({
  servidor,
  metrica,
  agenteOnline,
  aberto,
  onAbrir,
}: {
  servidor: Servidor
  metrica?: {
    cpuPercent: number | null
    memPercent: number | null
    diskPercent: number | null
    memTotal: string | null
    memUsada: string | null
    uptimeSec: number | null
    discos: Array<{ ponto: string; total: number; usado: number; percent: number }>
    idadeMin: number
  }
  agenteOnline: boolean
  aberto: boolean
  onAbrir: () => void
}) {
  // Metrica de mais de 3 min nao e "ao vivo": o agente coleta a cada 30 s.
  const fresca = metrica && metrica.idadeMin < 3
  const semAgente = !agenteOnline && !metrica

  return (
    <section className="rounded border border-line bg-panel shadow-sm">
      <button onClick={onAbrir} className="w-full border-b border-line px-4 py-3 text-left">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate font-medium text-ink">{servidor.hostname}</span>
          {agenteOnline ? (
            <Pill tone="ok">agente online</Pill>
          ) : metrica ? (
            <Pill tone="warn">agente offline</Pill>
          ) : (
            <Pill tone="neutral">sem agente</Pill>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-3">
          <span className="font-mono">{servidor.publicIp ?? '—'}</span>
          {metrica?.uptimeSec != null && <span>· no ar {uptimeHumano(metrica.uptimeSec)}</span>}
        </div>
      </button>

      <div className="space-y-3 p-4">
        {semAgente ? (
          <p className="text-sm text-ink-3">
            Sem agente instalado. Instale-o na aba Contas para ver CPU, memoria e disco daqui.
          </p>
        ) : !metrica ? (
          <p className="text-sm text-ink-3">Aguardando a primeira coleta...</p>
        ) : (
          <>
            {!fresca && (
              <p className="text-xs text-warn">
                Ultima coleta ha {metrica.idadeMin} min — o agente pode ter caido.
              </p>
            )}
            <Medidor label="CPU" valor={metrica.cpuPercent} />
            <Medidor label="Memoria" valor={metrica.memPercent} />
            {metrica.memTotal && (
              <p className="text-right text-[11px] text-ink-3">
                {bytesHumano(metrica.memUsada)} / {bytesHumano(metrica.memTotal)}
              </p>
            )}
            <Medidor label="Disco (mais cheio)" valor={metrica.diskPercent} />
          </>
        )}
      </div>

      {aberto && metrica && <DetalheMonitor serverId={servidor.id} discos={metrica.discos} />}
    </section>
  )
}

function DetalheMonitor({
  serverId,
  discos,
}: {
  serverId: string
  discos: Array<{ ponto: string; total: number; usado: number; percent: number }>
}) {
  const hist = useQuery(() => api.identity.metricsHistory.query({ serverId, horas: 24 }), [serverId])
  const pontos = hist.data ?? []

  return (
    <div className="space-y-4 border-t border-line bg-ground/40 p-4">
      <div>
        <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-ink-3">
          CPU — ultimas 24h
        </div>
        <Sparkline pontos={pontos.map((p) => p.cpu)} cor="var(--color-accent)" />
      </div>
      <div>
        <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-ink-3">
          Memoria — ultimas 24h
        </div>
        <Sparkline pontos={pontos.map((p) => p.mem)} cor="var(--color-ok)" />
      </div>

      {discos.length > 0 && (
        <div>
          <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-ink-3">Discos</div>
          <div className="space-y-2">
            {discos.map((d) => (
              <div key={d.ponto}>
                <Medidor label={d.ponto} valor={d.percent} />
                <p className="text-right text-[11px] text-ink-3">
                  {bytesHumano(d.usado)} / {bytesHumano(d.total)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {pontos.length === 0 && (
        <p className="text-xs text-ink-3">Sem historico ainda — os graficos aparecem apos algumas coletas.</p>
      )}

      <UsoAoVivo serverId={serverId} />
      <ProgramasInstalados serverId={serverId} />
    </div>
  )
}

/**
 * Uso ao vivo: os processos que mais consomem agora.
 *
 * Responde "o que esta pesando neste servidor" sem abrir o gerenciador de
 * tarefas por RDP. O agente manda o topo a cada minuto; a tela so mostra.
 */
function UsoAoVivo({ serverId }: { serverId: string }) {
  const uso = useQuery(() => api.identity.processes.query({ serverId }), [serverId], {
    pollMs: 30_000,
  })
  const d = uso.data

  return (
    <div>
      <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-ink-3">
        Uso agora{d ? ` — ${d.totalProcessos} processos` : ''}
      </div>
      {!d ? (
        <p className="text-xs text-ink-3">
          Sem dados de uso ainda — o agente coleta a cada minuto assim que conecta.
        </p>
      ) : d.itens.length === 0 ? (
        <p className="text-xs text-ink-3">Nenhum processo no ultimo retrato.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-ink-3">
              <th className="pb-1">Processo</th>
              <th className="pb-1 text-right">CPU</th>
              <th className="pb-1 text-right">Memoria</th>
            </tr>
          </thead>
          <tbody>
            {d.itens.map((p) => (
              <tr key={p.pid} className="border-t border-line/50">
                <td className="py-1 font-mono text-ink">{p.nome}</td>
                <td className="py-1 text-right text-ink-2">{Math.round(p.cpuPercent)}%</td>
                <td className="py-1 text-right text-ink-2">
                  {bytesHumano(p.memBytes)} · {Math.round(p.memPercent)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/**
 * Programas instalados neste servidor, com filtro.
 *
 * O inventario e um retrato do agente (a cada 6 h); a lista pode ser longa,
 * por isso rola dentro de uma caixa e tem busca. Vale para Windows (registro)
 * e Linux (pacotes) — a mesma tela, a mesma pergunta.
 */
function ProgramasInstalados({ serverId }: { serverId: string }) {
  const inv = useQuery(() => api.identity.software.query({ serverId }), [serverId])
  const [busca, setBusca] = useState('')
  const d = inv.data
  const itens = (d?.itens ?? []).filter((i) =>
    busca.trim() === ''
      ? true
      : `${i.nome} ${i.fabricante ?? ''}`.toLowerCase().includes(busca.toLowerCase()),
  )

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
          Programas instalados{d ? ` — ${d.total}` : ''}
        </div>
        {d && d.total > 0 && (
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="filtrar..."
            className="rounded border border-line bg-panel px-2 py-1 text-xs text-ink focus:border-accent focus:outline-none"
          />
        )}
      </div>
      {!d || d.total === 0 ? (
        <p className="text-xs text-ink-3">
          Nenhum inventario ainda — o agente envia a lista logo apos conectar, e depois a cada 6 h.
        </p>
      ) : (
        <div className="max-h-72 overflow-y-auto rounded border border-line/60">
          <table className="w-full text-xs">
            <tbody>
              {itens.map((i, idx) => (
                <tr key={`${i.nome}-${idx}`} className="border-b border-line/40 last:border-0">
                  <td className="px-2 py-1 text-ink">{i.nome}</td>
                  <td className="px-2 py-1 font-mono text-ink-3">{i.versao ?? ''}</td>
                  <td className="px-2 py-1 text-ink-3">{i.fabricante ?? ''}</td>
                </tr>
              ))}
              {itens.length === 0 && (
                <tr>
                  <td className="px-2 py-2 text-ink-3">Nada corresponde ao filtro.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
